// @ts-nocheck
import { BufferUtils, GLTF } from "@gltf-transform/core";

// ----------------------------------------------------------------------------
type ResolveFunc = (data: string) => void;

// ----------------------------------------------------------------------------
export class BinaryUtils {
    // ------------------------------------------------------------------------
    static async stripImageBuffers(json: GLTF.IGLTF, resources: Record<string, Uint8Array>): Promise<Uint8Array> {
        // glTF (hexa), 2(version), 12 (size of header so far)
        let header: Uint32Array = new Uint32Array(3);
        header[0] = 0x46546C67; // glTF in hexa
        header[1] = 2;
        header[2] = 12; // size 3 x 4 bytes

        // chunk 0 JSON chunk
        delete json.buffers![0].uri;

        const jsonText: string = JSON.stringify(json);
        const jsonEncodedText: Uint8Array = BufferUtils.encodeText(jsonText);
        const jsonChunkData: Uint8Array = BufferUtils.pad(jsonEncodedText, 0x20);
        const jsonChunkHeader: Uint32Array = new Uint32Array([jsonChunkData.byteLength, 0x4E4F534A]);
        const jsonChunk: Uint8Array = BufferUtils.concat([new Uint8Array(jsonChunkHeader.buffer), jsonChunkData]);
        header[2] += jsonChunk.byteLength;

        // chunk 1 Bin chunk
        const validBuffers: string = Object.keys(resources).find((uri: string) => uri.endsWith('.bin')) as string;
        const binBuffer: Uint8Array = resources[validBuffers];

        // If GLB has no binary resources, return early.
        if (!binBuffer || !binBuffer.byteLength) {
            return BufferUtils.concat([new Uint8Array(header.buffer), jsonChunk]);
        }

        const binChunkData: Uint8Array = BufferUtils.pad(binBuffer, 0x00);
        const binChunkHeader: ArrayBufferLike = new Uint32Array([binChunkData.byteLength, 0x004E4942]).buffer;
        const binChunk: Uint8Array = BufferUtils.concat([new Uint8Array(binChunkHeader), binChunkData]);
        header[2] += binChunk.byteLength;

        return BufferUtils.concat([new Uint8Array(header.buffer), jsonChunk, binChunk]);
    }

    // ------------------------------------------------------------------------
    static async convertBufferToBase64(buffer: ArrayBuffer, mime: string = "appplication/octet-stream"): Promise<string> {
        let promise: Promise<string>;
        if (typeof (FileReader) === "undefined") {
            // means we're most likely running under Node
            promise = Promise.resolve(`data:${mime};base64,${Buffer.from(buffer).toString("base64")}`);
        } else {
            promise = new Promise((fnResolve: ResolveFunc) => {
                let blob: Blob = new Blob([buffer], {
                    type: mime
                }),
                    reader: FileReader = new FileReader();

                reader.onload = (): void => {
                    fnResolve(reader.result as string);
                };
                reader.readAsDataURL(blob);
            });
        }

        return promise;
    }

    // ------------------------------------------------------------------------
    static async convertBufferToHeadlessBase64(buffer: ArrayBuffer): Promise<string> {
        let dataUri: string = await this.convertBufferToBase64(buffer);
        // strip dataUri Header
        return dataUri.replace(/^data:.*?;base64,/, "");
    }

    // ------------------------------------------------------------------------
    static async convertBase64ToBuffer(b64: string): Promise<ArrayBuffer> {
        let buffer: ArrayBuffer;

        // nodeRequire will be defined in node environments, not an error.
        // @ts-ignore
        if (global.nodeRequire) {
            const nodeBuffer: Buffer = Buffer.from(b64, "base64");
            // Uses buffer slice to extract only actual glTF data, avoiding garbage from buffer pool.
            buffer = nodeBuffer.buffer.slice(nodeBuffer.byteOffset, nodeBuffer.byteOffset + nodeBuffer.byteLength);
        } else {
            let dataUri: string = b64;
            if (!b64.startsWith("data:")) {
                dataUri = "data:application/octet-binary;base64," + dataUri;
            }

            let response: Response = await fetch(dataUri);

            buffer = await response.arrayBuffer();
        }

        return buffer;
    }

    // ------------------------------------------------------------------------
    static concatUint8Arrays(bufferA: Uint8Array, bufferB: Uint8Array): Uint8Array {
        let completeBuffer: Uint8Array = new Uint8Array(bufferA.byteLength + bufferB.byteLength);

        completeBuffer.set(bufferA, 0);
        completeBuffer.set(bufferB, bufferA.byteLength);

        return completeBuffer;
    }

    // ------------------------------------------------------------------------
    static convertUint8ArrayToString(buffer: Uint8Array, encoding?: string): string {
        return new TextDecoder(encoding).decode(buffer);
    }
}
